import { useMutation } from "@apollo/client";
import { downloadTranscriptsMutation } from "../modules/mutations";
import { useErrorHandler } from "TranscriptsV3/hooks";
import { useCallback } from "react";
import _ from "lodash";
import moment from "moment";
import { generateRandomId, formatDate } from "Utils";
import { useDispatch } from "Hooks";
import {
  downloadFromUrl,
  removeProgressElement,
  setProgress,
} from "modules/Services";
import { setToastMsg } from "Login/modules/LoginModule";
import { getUserInfo } from "Utils/UserDetailUtils";
import { formatSchoolLabels } from "../modules/helpers";
import { getFormattedUserName } from "Utils/GetFormattedUserName";
import { DATE_TIME_CONSTANTS } from "Constants/dateTimeConstants";
import { useTranscriptsContext } from "TranscriptsV3/context/TranscriptsContext";
import { useTranslation } from "react-i18next";

const getStructure = ({
  studentIds,
  studentIdToSchoolIdMap,
  schoolIdToSchoolDataMap,
  templateId,
}) => {
  if (_.size(studentIds) === 1) {
    return _.map(studentIds, studentId => ({
      entityId: studentId,
      entityType: "STUDENT",
      templateId,
    }));
  }

  const schoolIdToStudentIdsMap = _.reduce(
    studentIds,
    (result, studentId) => {
      const schoolId = studentIdToSchoolIdMap[studentId];
      if (schoolId) {
        result[schoolId] = result[schoolId] || [];
        result[schoolId].push(studentId);
      }
      return result;
    },
    {}
  );

  return _.map(schoolIdToStudentIdsMap, (studentIds, schoolId) => ({
    entityType: "CURRICULUM_PROGRAM",
    entityId: schoolId,
    templateId,
    label: formatSchoolLabels([schoolId], schoolIdToSchoolDataMap),
    downloadEntities: _.map(studentIds, studentId => ({
      entityId: studentId,
      entityType: "STUDENT",
      templateId,
    })),
  }));
};

const getUpdatedLabel = ({
  label,
  structure,
  studentIds,
  studentsData,
  selectedTemplateName,
  issueDate,
  accountProgramLabel,
}) => {
  if (label) return label;

  const formattedDate = formatDate({
    date: issueDate,
    format: DATE_TIME_CONSTANTS.DD_MMM_YYYY,
  });

  if (_.size(studentIds) === 1) {
    const student = _.find(studentsData?.multiNode, {
      id: _.first(studentIds),
    });
    const studentName = getFormattedUserName({ user: student });

    return `${selectedTemplateName}_${studentName}_${formattedDate}`;
  }

  if (_.size(structure) === 1) {
    return `${selectedTemplateName}_${structure[0].label}_${formattedDate}`;
  }

  return `${selectedTemplateName}_${accountProgramLabel}_${formattedDate}`;
};

const useDownloadTranscripts = () => {
  const onError = useErrorHandler();
  const [mutateFn] = useMutation(downloadTranscriptsMutation);
  const dispatch = useDispatch();
  const { accountProgramLabel } = useTranscriptsContext();
  const [, { language }] = useTranslation();

  return useCallback(
    async ({
      studentIds,
      studentIdToSchoolIdMap,
      issueDate,
      templateId,
      label,
      schoolIdToSchoolDataMap,
      studentsData,
      selectedTemplateName,
    }) => {
      const uploadId = generateRandomId();
      const mimeType =
        _.size(studentIds) > 1 ? "application/zip" : "application/pdf";

      const structure = getStructure({
        studentIds,
        studentIdToSchoolIdMap,
        schoolIdToSchoolDataMap,
        templateId,
      });

      const updatedLabel = getUpdatedLabel({
        label,
        structure,
        studentIds,
        studentsData,
        selectedTemplateName,
        issueDate,
        accountProgramLabel,
      });

      dispatch(
        setProgress({
          id: uploadId,
          attachment: {
            mimeType,
            name: updatedLabel,
          },
          progress: 0,
          status: "PROCESSING",
          cancelToken: () => {
            dispatch(removeProgressElement({ id: uploadId }));
          },
        })
      );
      const { data } = await mutateFn({
        variables: {
          input: {
            issueDate: formatDate({
              date: issueDate,
              format: DATE_TIME_CONSTANTS.YYYY_MM_DD,
            }),
            label: updatedLabel,
            downloadEntities: structure,
            language,
          },
        },
        optimisticResponse: {
          transcript: {
            generateTranscriptDownloadUrl: {
              filename: updatedLabel,
              url: "",
              isLongRunningTaskCreated: false,
              __typename: "GenerateTranscriptDownloadUrlResponse",
            },
            __typename: "TranscriptMutations",
          },
        },
        update: cache => {
          _.forEach(studentIds, studentId => {
            const cacheId = cache.identify({
              __typename: "Student",
              id: studentId,
            });

            cache.modify({
              id: cacheId,
              fields: {
                transcriptSummary: existingRef => {
                  if (!existingRef?.__ref) return existingRef;

                  cache.modify({
                    id: existingRef.__ref,
                    fields: {
                      lastTranscriptGeneratedAt() {
                        return moment().toISOString();
                      },
                    },
                  });

                  return existingRef;
                },
              },
            });
          });
        },
        onError: () => {
          dispatch(removeProgressElement({ id: uploadId }));
          onError();
        },
      });

      const { url, filename, isLongRunningTaskCreated } = _.get(
        data,
        "transcript.generateTranscriptDownloadUrl",
        {}
      );

      if ((url && filename) || isLongRunningTaskCreated) {
        if (isLongRunningTaskCreated) {
          dispatch(removeProgressElement({ id: uploadId }));
          dispatch(
            setToastMsg({
              type: "info",
              msg: "toastMsgs:send_email_with_download_link",
              hasHTMLTags: true,
              locale_params: [
                {
                  key: "email",
                  value: getUserInfo()?.email,
                  isPlainText: true,
                },
              ],
              timeOut: 3000,
            })
          );
        } else {
          dispatch(
            downloadFromUrl({
              fileUploadId: uploadId,
              attachment: {
                url: url,
                name: filename,
                mimeType,
              },
              shouldAskForSave: true,
            })
          );
        }
      }
    },
    [accountProgramLabel, dispatch, mutateFn, onError, language]
  );
};

export default useDownloadTranscripts;
